import { useState, useCallback, useMemo, memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye, Pencil, ChevronDown, Check, Plus, Trash2 } from 'lucide-react';
import { generateExercice } from '../../generators';
import { getQuestionTypeLabel } from '../../utils/questionUtils';
import QuestionForm from './QuestionForm';
import ExercicePreview from './ExercicePreview';
import IconesModes from './IconesModes';
// Apparence des formats + helpers partagés avec BanqueQuestionCard
import { getArticle, formatDateCourte, stripHtml } from './formatConfig';

// Rendu complet de l'exercice — extrait dans ExercicePreview.jsx (2.4c),
// partagé avec les cartes de la banque EvalNat (QuestionCardEvalNat).

/**
 * Card d'exercice généré — affiche l'exercice complet inline.
 * Header coloré par type, corps = énoncé + réponses, footer = pills format avec compteurs.
 *
 * En mode selectable, chaque pill de format se split en deux zones :
 *   - Zone gauche : label (clic = switch preview)
 *   - Zone droite : +/- compteur (clic = ajuster le nombre d'instances)
 *
 * Trois options (11/10/2026, QCM de suivi de l'évaluation nationale, vers une interface commune avec ce
 * composeur) ; absentes, la carte se comporte exactement comme avant :
 *   - genererExercice(formatKey) : tire l'exercice par cette fonction au lieu du registre des attendus ;
 *   - varianteUnique : une seule variante (compréhension de l'écrit) : pas d'autre variante, un seul ajout ;
 *   - sansModes : pas d'icônes de passation sur les onglets (le QCM de suivi ne se joue pas aux cartes) ;
 *   - enTeteEdition : affiché en haut de la fenêtre « Modifier » (le texte d'une question de lecture).
 * Et onUpdateExercice peut rendre false pour refuser un ajout : la carte garde alors son aperçu.
 */

const ExerciseurCard = ({
    attenduId, category, niveau, matiere, niveauChar, selectable, exoKeyPrefix,
    selectedExercices, onUpdateExercice, onPreview, descName, attenduDescription,
    generesParFormat, selectedIds, onToggleSelect, onDeleteQuestion,
    modeRevue = false, genererExercice = null, varianteUnique = false, sansModes = false, enTeteEdition = null,
    destination = null,
}) => {
    // Un tirage du format : par la fonction donnée, sinon par le registre des attendus
    const tirer = useCallback(
        (formatKey) => (genererExercice ? genererExercice(formatKey) : generateExercice(attenduId, formatKey)),
        [genererExercice, attenduId]
    );
    const [activeFormat, setActiveFormat] = useState(null);
    // L'attendu tel que la carte le connaît, pour le libellé du formulaire
    // d'édition : un attendu d'un autre niveau que la classe n'est pas dans
    // l'arbre de la classe que ce formulaire consulte (2026-09-29).
    const attenduConnu = useMemo(
        () => ({ id: attenduId, description: attenduDescription, descripteurNom: descName }),
        [attenduId, attenduDescription, descName]
    );
    const [isEditing, setIsEditing] = useState(false);
    const [customPayload, setCustomPayload] = useState(null);
    const [flashKey, setFlashKey] = useState(0);
    const [exercice, setExercice] = useState(() => {
        // On passe le 1er format.key (pas category.id) pour garantir une clé de générateur
        // valide : la convention est `{category_id}_{suffix}` et `generate()` cherche une
        // correspondance exacte, ce qui échoue si on passe l'id de catégorie seul.
        const defaultKey = category?.formatsList?.[0]?.key || category.id;
        try { return tirer(defaultKey); }
        catch { return null; }
    });

    const displayExercice = customPayload || exercice;
    const { formatsList } = category;
    // Clé du format courant : l'onglet cliqué, sinon celle que porte l'instance
    // SI c'est bien un format de la carte. Certains générateurs posaient l'id de
    // la CARTE en generatorKey : « + Ajouter » rangeait alors l'exercice sous
    // une clé inconnue de la carte et le compteur restait muet tant qu'on
    // n'avait pas cliqué un onglet (bug récurrent, tranché 2026-09-02). Repli :
    // le premier format de la carte, celui de la génération initiale.
    const cleInstance = exercice?._meta?.generatorKey;
    const currentKey = activeFormat
        || (formatsList.some((f) => f.key === cleInstance) ? cleInstance : formatsList[0]?.key);
    const [showTooltip, setShowTooltip] = useState(false);
    const [spinning, setSpinning] = useState(false);
    // Popover « réutiliser » : rect d'ancrage du ▾ (null = fermé)
    const [reuseAnchor, setReuseAnchor] = useState(null);

    // Instances déjà persistées de CE format (couple attendu + generator_key)
    const dejaCrees = useMemo(
        () => generesParFormat?.get(activeFormat || exercice?._meta?.generatorKey) || [],
        [generesParFormat, activeFormat, exercice]
    );

    // Fermeture du popover à Échap
    useEffect(() => {
        if (!reuseAnchor) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setReuseAnchor(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [reuseAnchor]);

    // Dériver les counts par format depuis le Map parent
    const formatCounts = useMemo(() => {
        const counts = {};
        if (!selectedExercices || !exoKeyPrefix) return counts;
        formatsList.forEach(f => {
            const entry = selectedExercices.get(`${exoKeyPrefix}_${f.key}`);
            if (entry) counts[f.key] = entry.payloads.length;
        });
        return counts;
    }, [selectedExercices, exoKeyPrefix, formatsList]);

    const totalCount = useMemo(() =>
        Object.values(formatCounts).reduce((sum, c) => sum + c, 0)
    , [formatCounts]);

    const isSelected = totalCount > 0;

    const handleReload = useCallback((e) => {
        if (e) e.stopPropagation();
        setCustomPayload(null);
        setSpinning(true);
        // Avec un générateur donné, l'onglet affiché fait foi (sa clé d'instance n'est pas un format)
        const format = genererExercice ? currentKey : (activeFormat || exercice?._meta?.generatorKey || category.id);
        try {
            setExercice(tirer(format));
            setFlashKey(k => k + 1);
        } catch { /* ignore */ }
        setTimeout(() => setSpinning(false), 600);
    }, [tirer, genererExercice, currentKey, activeFormat, exercice, category.id]);

    const handleSelectFormat = useCallback((formatKey) => {
        setActiveFormat(formatKey);
        setCustomPayload(null);
        try { setExercice(tirer(formatKey)); }
        catch { /* ignore */ }
    }, [tirer]);

    const handleIncrement = useCallback((formatKey) => {
        const key = `${exoKeyPrefix}_${formatKey}`;
        const current = selectedExercices?.get(key)?.payloads?.length || 0;
        try {
            // « Ajouter CE QCM » = WYSIWYG strict : on ajoute l'exercice
            // AFFICHÉ (personnalisé via QuestionForm ou instance générée),
            // puis l'aperçu passe à une instance fraîche pour le clic
            // suivant. L'ancien code régénérait AVANT d'ajouter → la
            // personnalisation du prof partait à la poubelle et une version
            // aléatoire était donnée en classe/papier à sa place.
            const affiche = customPayload || exercice;
            if (!affiche) return;
            // Contexte pédagogique (descripteur + libellé d'attendu, connus
            // ici seulement) + provenance réinjectée : QuestionForm en
            // draftMode ne renvoie pas _meta → on la reprend de l'instance
            // d'origine (generatorKey → rangement exerciseur conservé).
            const payload = {
                ...affiche,
                attendu: affiche.attendu ?? exercice?.attendu ?? attenduId,
                niveau: affiche.niveau ?? exercice?.niveau,
                matiere: affiche.matiere ?? exercice?.matiere,
                _meta: {
                    ...(exercice?._meta || {}),
                    ...(affiche._meta || {}),
                    ...(descName ? { descripteur: descName } : {}),
                    ...(attenduDescription ? { attenduTitre: attenduDescription } : {}),
                },
            };
            // La page peut refuser l'ajout en rendant false (QCM de suivi : un exercice modifié qu'une
            // variante ne sait pas porter) : l'aperçu reste alors tel quel, personnalisation comprise
            if (onUpdateExercice?.(key, current + 1, payload) === false) return;
            // L'aperçu s'actualise vers une NOUVELLE instance du format
            setActiveFormat(formatKey);
            // Une variante unique reste affichée telle qu'ajoutée (personnalisation comprise)
            if (!varianteUnique) {
                setCustomPayload(null);
                try { setExercice(tirer(formatKey)); }
                catch { /* génération impossible : on garde l'aperçu actuel */ }
            }
            // Tooltip premier usage — s'affiche une seule fois (localStorage) ; il parle de versions
            // différentes, donc pas sur une variante unique
            if (!varianteUnique && !localStorage.getItem('tanoed_tooltip_format_shown')) {
                setShowTooltip(true);
                localStorage.setItem('tanoed_tooltip_format_shown', '1');
            }
        } catch { /* ignore */ }
    }, [attenduId, tirer, varianteUnique, exoKeyPrefix, selectedExercices, onUpdateExercice, descName, attenduDescription, customPayload, exercice]);

    if (!displayExercice) return null;

    const typeLabel = getQuestionTypeLabel(displayExercice);
    const isModified = !!customPayload;

    return (
        <>
            <div className={`bg-white rounded-[18px] overflow-hidden break-inside-avoid transition-all duration-[250ms] ${
                isSelected
                    ? 'border-[1.5px] border-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12),0_2px_8px_rgba(0,0,0,0.04)]'
                    : 'border-[1.5px] border-gray-200 shadow-sm hover:shadow-md'
            }`}>

                {/* ── Header : titre (+ compteur si format unique) + actions ── */}
                <div className="pt-5 px-[22px] pb-3.5 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        <h4 className="text-[15px] font-semibold text-slate-900 leading-relaxed tracking-tight truncate">{category.label}</h4>
                        {/* Clé du format courant (trace exceptionnelle, demande prof
                            2026-07-28) : identifie la carte comme dans les rapports
                            de correction (gen_XXX__format) pour piocher sans erreur.
                            Visible en « Mode revue » seulement (allègement
                            2026-09-02) : information de crible, pas de prof. */}
                        {modeRevue && currentKey ? (
                            <span className="block font-mono text-[9px] text-slate-400 truncate select-all">
                                {currentKey}
                            </span>
                        ) : null}
                        {isModified && <span className="text-[9px] text-amber-500 font-medium">Personnalisé</span>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {/* Un seul compteur par carte (allègement 2026-09-03) : celui
                            des onglets de format. Quand la carte n'a qu'un format, pas
                            d'onglets, la même pastille vient ici. */}
                        {formatsList.length <= 1 && totalCount > 0 ? (
                            <span
                                key={totalCount}
                                className="text-[10px] px-2 py-0.5 rounded-full text-white font-bold bg-blue-600"
                                style={{ animation: 'badgePulse 0.3s ease-out' }}
                                title={`${totalCount} ajouté${totalCount > 1 ? 's' : ''}`}
                            >
                                {totalCount}
                            </span>
                        ) : null}
                        {/* Actions directes (un clic) : aperçu élève, personnaliser */}
                        {onPreview && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // 2e argument : regénérateur « Autre variante » (même
                                    // attendu + format courant) pour l'aperçu interactif.
                                    const key = currentKey || category.id;
                                    onPreview(displayExercice, () => {
                                        try { return tirer(key); }
                                        catch { return null; }
                                    });
                                }}
                                title="Voir l'exercice comme un élève, en interactif"
                                aria-label="Aperçu élève"
                                className="h-7 px-2 rounded-md hover:bg-gray-100 flex items-center gap-1 transition text-gray-500 hover:text-gray-800 text-xs font-medium"
                            >
                                <Eye size={14} />
                                <span className="hidden sm:inline">Aperçu élève</span>
                            </button>
                        )}
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                            title="Modifier cet exercice avant de l'ajouter"
                            aria-label="Modifier"
                            className="h-7 px-2 rounded-md hover:bg-gray-100 flex items-center gap-1 transition text-gray-500 hover:text-gray-800 text-xs font-medium"
                        >
                            <Pencil size={14} />
                            <span className="hidden sm:inline">Modifier</span>
                        </button>
                    </div>
                </div>

                {/* ── Onglets de format ── */}
                {formatsList.length > 1 && (
                    <div className="px-[22px] pb-3.5 flex items-center gap-1.5 overflow-x-auto">
                        {/* Annoncés : ce sont des onglets (l'aperçu change), pas des étiquettes. */}
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 shrink-0 mr-0.5">Formats</span>
                        {formatsList.map(f => {
                            const isActive = currentKey === f.key;
                            const count = formatCounts[f.key] || 0;
                            const fLabel = f.label;

                            // Palette à trois rôles (2026-09-02) : l'onglet actif est en
                            // bleu marine (sélection), les autres gris ; plus une couleur
                            // ni d'icône par format (2026-09-03) : le libellé suffit, seules
                            // les icônes de passation apportent une information.
                            return (
                                <button
                                    key={f.key}
                                    onClick={(e) => { e.stopPropagation(); handleSelectFormat(f.key); }}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
                                        isActive
                                            ? 'border-2 border-[#0f2a4a] bg-blue-50 text-[#0f2a4a]'
                                            : 'border-[1.5px] border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                                    }`}
                                >
                                    {fLabel}
                                    {/* Où ce format se joue (cartes, en ligne, papier) :
                                        le prof le voit avant de choisir le mode. */}
                                    {sansModes ? null : <IconesModes format={f} className="ml-0.5" />}
                                    {count > 0 && (
                                        <span className="text-[9px] px-1.5 rounded-full text-white font-bold bg-blue-600">
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* ── Zone preview ── */}
                <div className="mx-4 rounded-[14px] overflow-hidden border-[1.5px] border-gray-200 bg-[#fafbfc]">
                    <div
                        key={`${currentKey}-${flashKey}`}
                        className="px-4 pt-4 pb-2 relative"
                        style={{ animation: 'fadeSwap 0.3s ease' }}
                    >
                        <div style={{ zoom: 0.88 }} className="text-[13px] overflow-hidden">
                            <ExercicePreview question={displayExercice} />
                        </div>
                    </div>
                </div>

                {/* ── Actions : « Voir une autre variante » à gauche (lien),
                    « + Ajouter » court à droite, sans rappel du compte (le
                    compteur d'onglet suffit, allègement 2026-09-03). ── */}
                <div className="pt-3 px-[22px] pb-4">
                    <div className="flex items-center gap-2">
                        {varianteUnique ? (
                            <span className="px-2 py-1.5 text-xs text-gray-500 shrink-0">Une seule variante pour cet item</span>
                        ) : (
                            <button
                                onClick={handleReload}
                                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-blue-600 text-xs font-medium hover:bg-blue-50 transition-all cursor-pointer shrink-0"
                            >
                                <span className={`text-base transition-transform duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${spinning ? 'rotate-[360deg]' : ''}`}>
                                    ⟳
                                </span>
                                Voir une autre variante
                            </button>
                        )}
                        <div className="flex-1" />
                        {selectable && (
                            <div className="flex shrink-0">
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleIncrement(currentKey); }}
                                    disabled={varianteUnique && totalCount > 0}
                                    title={`Ajouter ${getArticle(typeLabel)} ${typeLabel} à la session (chaque clic ajoute une version différente)`}
                                    className={`px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition disabled:opacity-60 disabled:hover:bg-blue-600 ${
                                        dejaCrees.length > 0 ? 'rounded-l-lg' : 'rounded-lg'
                                    }`}
                                >
                                    {/* Mode groupes : le groupe visé dans « Ajouter à »,
                                        lisible là où l'on clique (2026-10-04). */}
                                    {varianteUnique && totalCount > 0 ? 'Ajoutée' : destination ? `+ Ajouter à ${destination}` : '+ Ajouter'}
                                </button>
                                {/* Split ▾ : réutiliser un exercice déjà donné (ce format) —
                                    visible seulement s'il en existe. Popover portalisé
                                    (la carte est overflow-hidden). */}
                                {dejaCrees.length > 0 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const r = e.currentTarget.getBoundingClientRect();
                                            setReuseAnchor((prev) => (prev ? null : r));
                                        }}
                                        title={`Réutiliser un exercice déjà donné (${dejaCrees.length})`}
                                        aria-label="Réutiliser un exercice déjà donné"
                                        className="px-2 bg-blue-600 hover:bg-blue-700 text-white rounded-r-lg border-l border-blue-500/60 transition flex items-center"
                                    >
                                        <ChevronDown size={14} className={`transition-transform ${reuseAnchor ? 'rotate-180' : ''}`} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Tooltip premier usage */}
                {showTooltip && (
                    <div className="mx-[22px] mb-5 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700 flex items-start gap-2">
                        <span className="flex-1">Chaque clic ajoute une version avec des <strong>valeurs différentes</strong>. Cliquez « Voir une autre variante » pour voir d'autres valeurs avant d'ajouter.</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowTooltip(false); }}
                            className="text-blue-400 hover:text-blue-600 font-semibold flex-shrink-0"
                        >
                            OK
                        </button>
                    </div>
                )}
            </div>

            {/* Popover « Réutiliser un exercice déjà donné » — portalisé,
                ancré au-dessus du ▾, fermé par clic extérieur ou Échap */}
            {reuseAnchor && createPortal(
                <>
                    <div className="fixed inset-0 z-[10002]" onClick={() => setReuseAnchor(null)} />
                    <div
                        className="fixed z-[10003] w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
                        style={{
                            left: Math.max(8, Math.min(reuseAnchor.right - 320, window.innerWidth - 328)),
                            bottom: window.innerHeight - reuseAnchor.top + 6,
                        }}
                    >
                        <p className="px-3 pt-2.5 pb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                            Réutiliser un exercice déjà donné
                        </p>
                        <div className="max-h-64 overflow-y-auto py-1">
                            {dejaCrees.map((q) => {
                                const coche = selectedIds?.has(q.id);
                                return (
                                    <div key={q.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onToggleSelect?.(q.id); }}
                                            className={`flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0 transition ${
                                                coche
                                                    ? 'bg-emerald-500 text-white'
                                                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                            }`}
                                            aria-label={coche ? 'Retirer de la sélection' : 'Ajouter à la sélection'}
                                        >
                                            {coche ? <Check size={13} /> : <Plus size={13} />}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-700 truncate">{stripHtml(q.texte).slice(0, 60) || '(énoncé visuel)'}</p>
                                            <p className="text-[10px] text-gray-400">{formatDateCourte(q.date_creation)}</p>
                                        </div>
                                        {onPreview && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onPreview(q); }}
                                                className="w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0"
                                                aria-label="Aperçu"
                                            >
                                                <Eye size={13} />
                                            </button>
                                        )}
                                        {onDeleteQuestion && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onDeleteQuestion(q.id); }}
                                                className="w-6 h-6 rounded-md hover:bg-red-50 flex items-center justify-center text-gray-300 hover:text-red-500 flex-shrink-0 transition"
                                                aria-label="Supprimer cet exercice"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>,
                document.body
            )}

            {/* Modal d'édition — QuestionForm en draftMode */}
            {isEditing && createPortal(
                <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setIsEditing(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto p-5" onClick={e => e.stopPropagation()}>
                        {enTeteEdition}
                        <QuestionForm
                            question={displayExercice}
                            niveau={niveau}
                            matiere={matiere}
                            niveauChar={niveauChar}
                            attenduConnu={attenduConnu}
                            draftMode
                            onSubmit={(data) => {
                                setCustomPayload(data);
                                setIsEditing(false);
                            }}
                            onCancel={() => setIsEditing(false)}
                        />
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default memo(ExerciseurCard);
